import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { apiClient, ApiError, NotAuthenticatedError } from "../api/client.js";
import type { AssetDetailResponse } from "../api/types.js";
import { AssetHistoryTab } from "./asset-history-tab.js";
import { basename, formatBytes, isVideoPath, thumbnailLabel } from "./asset-helpers.js";
import { AssetThumbnail } from "./asset-thumbnail.js";
import { classifyLocation } from "./location-helpers.js";
import { MediaPanel } from "./media-panel.js";
import { parentDir } from "./path-breadcrumb.js";
import { VideoFrameStrip } from "./video-frame-strip.js";

type TabId = "yleiset" | "sijainnit" | "historia";

// 1.D.7 Asset-detail modal. Avautuu kun assetId !== null.
// Lataa GET /assets/:id, nayttaa metadata + tagit + versiot + sijainnit
// (Mac/R2/USB-tilat). ESC-nappi ja overlay-click sulkevat.

export type AssetDetailModalProps = {
  assetId: string | null;
  onClose: () => void;
};

export function AssetDetailModal({ assetId, onClose }: AssetDetailModalProps) {
  const [, navigate] = useLocation();
  const [data, setData] = useState<AssetDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [tab, setTab] = useState<TabId>("yleiset");
  const [machineId, setMachineId] = useState<string | null>(null);

  // 3.B.2.2: kun modaali avautuu, hae aktiivinen daemon-machineId.
  // Yksittaisen daemonin tapauksessa otetaan ensimmainen — multi-machine
  // tukea ei vielae ole asset-history-puolella.
  useEffect(() => {
    if (assetId === null) {
      setMachineId(null);
      return;
    }
    let cancelled = false;
    apiClient
      .listMachines()
      .then((res) => {
        if (cancelled) return;
        const first = res.machines[0];
        setMachineId(first?.id ?? null);
      })
      .catch(() => {
        if (!cancelled) setMachineId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [assetId]);

  // Tab resetoidaan kun modaali avataan eri assetille.
  useEffect(() => {
    setTab("yleiset");
  }, [assetId]);

  useEffect(() => {
    if (assetId === null) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .getAsset(assetId)
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof NotAuthenticatedError) setError("Ei kirjautunut sisaan.");
        else if (err instanceof ApiError) setError(err.message);
        else setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [assetId]);

  useEffect(() => {
    if (assetId === null) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [assetId, onClose]);

  if (assetId === null) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 px-4 py-10"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-3xl rounded-lg border border-slate-800 bg-slate-950 p-6 shadow-2xl">
        <header className="mb-4 flex items-start justify-between gap-4">
          <h3 className="truncate text-lg font-semibold tracking-tight text-slate-100">
            {data ? basename(data.asset.currentPath) : "Lataa…"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sulje"
            className="rounded-md px-2 py-1 text-slate-400 hover:bg-slate-900 hover:text-slate-100"
          >
            ✕
          </button>
        </header>

        {isLoading && <p className="text-slate-500">Ladataan…</p>}
        {error && (
          <p className="rounded-md border border-red-700 bg-red-950/40 px-3 py-2 text-sm text-red-200">
            {error}
          </p>
        )}
        {data && (
          <div className="flex flex-col gap-4">
            <nav className="flex gap-1 border-b border-slate-800" role="tablist">
              {(
                [
                  { id: "yleiset", label: "Yleiset" },
                  { id: "sijainnit", label: "Sijainnit" },
                  { id: "historia", label: "Historia" },
                ] as Array<{ id: TabId; label: string }>
              ).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  onClick={() => setTab(t.id)}
                  className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
                    tab === t.id
                      ? "border-cyan-500 text-slate-100"
                      : "border-transparent text-slate-400 hover:text-slate-200"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </nav>

            {tab === "yleiset" && (
              <div className="flex flex-col gap-6">
                <section className="flex flex-col gap-4 md:flex-row md:items-start">
                  <div className="md:w-1/3">
                    <AssetThumbnail
                      assetId={data.asset.id}
                      fallbackLabel={thumbnailLabel(data.asset.currentPath)}
                      size={1024}
                      alt={basename(data.asset.currentPath)}
                      isVideo={
                        isVideoPath(data.asset.currentPath) ||
                        Boolean(data.asset.videoCodec)
                      }
                      className="grid aspect-square place-items-center overflow-hidden rounded-md bg-slate-800 text-2xl font-medium text-slate-300"
                    />
                  </div>
                  <div className="md:w-2/3">
                    <h4 className={sectionTitle}>Asset</h4>
                    <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm">
                      <Row label="Polku" value={data.asset.currentPath} />
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => {
                            const parent = parentDir(data.asset.currentPath);
                            navigate(`/selaa?path=${encodeURIComponent(parent)}`);
                            onClose();
                          }}
                          className="text-xs text-cyan-400 underline hover:text-cyan-300"
                          title="Avaa /selaa kansiopolun rajoituksella"
                        >
                          ↑ Porautuu kansioon
                        </button>
                      </div>
                      <Row label="Tier" value={data.asset.currentTier} />
                      <Row label="Meta-status" value={data.asset.currentMetaStatus} />
                      <Row label="Koko" value={formatBytes(data.asset.sizeBytes ?? null)} />
                      {/* 2.G.3 — kuvattu vs. indeksoitu erottelu */}
                      {data.asset.capturedAt && (
                        <Row label="Kuvattu" value={data.asset.capturedAt} />
                      )}
                      <Row label="Indeksoitu krattiin" value={data.asset.createdAt} />
                      {data.asset.projectId && (
                        <Row label="Projekti" value={data.asset.projectId} />
                      )}
                    </dl>
                  </div>
                </section>

                {/* 2.G.2 — media-paneeli per mediatyyppi */}
                <section>
                  <MediaPanel asset={data.asset} />
                </section>

                {/* UM.7 — video-kehykset rinnakkain (5/25/50/75/90 %) */}
                {isVideoPath(data.asset.currentPath) && (
                  <section>
                    <h4 className={sectionTitle}>Video-kehykset</h4>
                    <p className="mt-1 text-xs text-slate-500">
                      Klikkaa kehysta avataksesi sen suuremmassa koossa.
                    </p>
                    <div className="mt-3">
                      <VideoFrameStrip assetId={data.asset.id} />
                    </div>
                  </section>
                )}

                {data.tags.length > 0 && (
                  <section>
                    <h4 className={sectionTitle}>Tagit</h4>
                    <ul className="mt-2 flex flex-wrap gap-2">
                      {data.tags.map((t) => (
                        <li
                          key={t}
                          className="rounded-full border border-slate-700 bg-slate-900 px-3 py-1 text-xs text-slate-200"
                        >
                          {t}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {data.metadata.length > 0 && (
                  <section>
                    <h4 className={sectionTitle}>Metadata</h4>
                    <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm md:grid-cols-2">
                      {data.metadata.map((m) => (
                        <Row key={m.id} label={m.fieldLabel} value={formatMetaValue(m.value)} />
                      ))}
                    </dl>
                  </section>
                )}
              </div>
            )}

            {tab === "sijainnit" && (
              <section>
                <h4 className={sectionTitle}>Sijainnit</h4>
                {data.locations.length === 0 ? (
                  <p className="mt-2 text-sm text-slate-500">Ei vielä yhtään snapshot-sijaintia.</p>
                ) : (
                  <ul className="mt-2 flex flex-col gap-2 text-sm">
                    {data.locations.map((loc, i) => {
                      const view = classifyLocation(loc);
                      return (
                        <li
                          key={`${loc.snapshotId}-${i}`}
                          className="flex items-center justify-between gap-3 rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2"
                        >
                          <div className="flex items-center gap-3">
                            <span
                              className={`inline-flex w-12 justify-center rounded bg-slate-800 px-2 py-1 text-xs font-medium uppercase tracking-wide ${labelColor(view.kind)}`}
                            >
                              {view.label}
                            </span>
                            <span className="truncate text-slate-300">{view.description}</span>
                          </div>
                          <span className="hidden text-xs text-slate-500 md:inline">
                            {loc.startedAt}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            )}

            {tab === "historia" && (
              <section>
                <h4 className={sectionTitle}>Snapshot-historia</h4>
                <p className="mt-1 mb-3 text-xs text-slate-500">
                  Daemon listaa kaikki snapshotit jotka kattavat tämän tiedoston
                  polun. Voit palauttaa tiedoston valittuun versioon halutessasi.
                </p>
                <AssetHistoryTab
                  assetId={data.asset.id}
                  assetPath={data.asset.currentPath}
                  assetCreatedAt={data.asset.createdAt}
                  machineId={machineId}
                />
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const sectionTitle = "text-xs font-semibold uppercase tracking-wide text-slate-400";

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="truncate text-slate-200" title={value}>
        {value}
      </dd>
    </>
  );
}

function labelColor(kind: ReturnType<typeof classifyLocation>["kind"]): string {
  switch (kind) {
    case "object_storage":
      return "text-emerald-300";
    case "usb":
      return "text-amber-300";
    case "mac":
      return "text-sky-300";
    default:
      return "text-slate-300";
  }
}

function formatMetaValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}
